import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API_BASE } from '@/lib/utils';
import { saveAuthSession } from '@/lib/auth-session';
import { SIGNUP_DOCUMENT_TITLE } from '@/lib/tenant-brand';
import AuthPageShell from '@/components/AuthPageShell';
import SocialAuthButtons from '@/components/SocialAuthButtons';
import OtpCodeInput from '@/components/OtpCodeInput';
import { verifyIdentity } from '@/lib/social-auth';
import PasswordField from '@/components/PasswordField';
import { passwordPolicyError } from '@/lib/password-policy';
import { formatMoneyWithPeriod } from '@/lib/money-display';
import { apiErrorDetail, uiDebugLog } from '@/lib/ui-debug-log';

const FALLBACK_PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    annualPriceSgd: 1188,
    tag: null,
    featured: false,
    limits: { eventsPerYear: 5, attendeesPerEvent: 500 },
  },
  {
    id: 'studio',
    name: 'Studio',
    annualPriceSgd: 2880,
    tag: 'Most popular',
    featured: true,
    limits: { eventsPerYear: 20, attendeesPerEvent: 3000 },
  },
  {
    id: 'scale',
    name: 'Scale',
    annualPriceSgd: 7200,
    tag: null,
    featured: false,
    limits: { eventsPerYear: 999, attendeesPerEvent: 15000 },
  },
];

const SELF_SERVE_IDS = new Set(['starter', 'studio', 'scale']);
const ANNUAL_DISCOUNT = 0.17;

function normalizePlanId(raw) {
  const id = String(raw || '').trim().toLowerCase();
  if (id === 'pro') return 'studio';
  return SELF_SERVE_IDS.has(id) ? id : 'studio';
}

function planPrices(plan) {
  const annual = Number(plan?.annualPriceSgd) || 0;
  const monthly = Math.round((annual / 12) * 100) / 100;
  const annualDiscounted = Math.round(annual * (1 - ANNUAL_DISCOUNT));
  return { monthly, annualDiscounted, annual };
}

function planLimitLine(plan) {
  const events = Number(plan?.limits?.eventsPerYear) || 0;
  const attendees = Number(plan?.limits?.attendeesPerEvent) || 0;
  const eventsLabel = events >= 999 ? 'Unlimited events' : `${events} events/yr`;
  const attendeesLabel = attendees ? `${attendees.toLocaleString()} attendees/event` : '';
  return [eventsLabel, attendeesLabel].filter(Boolean).join(' · ');
}

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPlan = normalizePlanId(searchParams.get('plan'));
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    orgName: '',
    ownerName: '',
    billingCycle: 'monthly',
    plan: initialPlan,
  });
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('details');
  const [otp, setOtp] = useState('');
  const [plans, setPlans] = useState(FALLBACK_PLANS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/pricing`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const selfServe = (data.plans || []).filter(
          (p) => SELF_SERVE_IDS.has(p.id) && p.selfServeCheckout !== false,
        );
        if (!cancelled && selfServe.length) setPlans(selfServe);
      } catch {
        /* keep defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fromQuery = normalizePlanId(searchParams.get('plan'));
    setForm((prev) => (prev.plan === fromQuery ? prev : { ...prev, plan: fromQuery }));
  }, [searchParams]);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === form.plan) || plans[0] || FALLBACK_PLANS[1],
    [plans, form.plan],
  );
  const selectedPrices = planPrices(selectedPlan);

  const finishSignup = (data, via) => {
    saveAuthSession({
      email: data.email,
      token: data.token,
      via,
      role: data.role || 'owner',
      tenant: data.tenantId || data.tenant,
    });
    const planName = String(data.plan || form.plan || 'studio');
    const label = planName.charAt(0).toUpperCase() + planName.slice(1);
    const trialEnd = data.trialEndsAt ? new Date(data.trialEndsAt).toLocaleDateString() : 'soon';
    toast.success(`${label} trial started`, {
      description: data.billingSetupUrl
        ? `Add a payment method — billing starts automatically after ${trialEnd}.`
        : `Trial ends ${trialEnd}.`,
    });
    if (data.billingSetupUrl) {
      sessionStorage.setItem('aivoryems.postOnboardingBillingUrl', data.billingSetupUrl);
    }
    window.location.assign('/admin/onboarding');
  };

  const handleSocialToken = async (provider, idToken) => {
    setBusy(true);
    try {
      const data = await verifyIdentity({
        provider,
        idToken,
        intent: 'signup',
        orgName: String(form.orgName || '').trim(),
        ownerName: String(form.ownerName || '').trim(),
        billingCycle: form.billingCycle,
        plan: form.plan,
      });
      finishSignup(data, provider);
    } catch (err) {
      if (err.code === 'account_exists') {
        toast.message('Account already exists', { description: 'Sign in with your existing credentials.' });
        navigate(`/login?email=${encodeURIComponent(err.email || form.email)}`);
        return;
      }
      toast.error(err?.message || 'Sign-up failed');
    } finally {
      setBusy(false);
    }
  };

  const requestOtp = async (e) => {
    e.preventDefault();
    const email = String(form.email || '').trim();
    const password = String(form.password || '');
    const confirmPassword = String(form.confirmPassword || '');
    const orgName = String(form.orgName || '').trim();
    const policyErr = passwordPolicyError(password);
    if (policyErr) {
      toast.error(policyErr);
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (orgName.length < 2) {
      toast.error('Organization name is required');
      return;
    }
    if (!SELF_SERVE_IDS.has(form.plan)) {
      toast.error('Choose a package to continue');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/signup/request-otp`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          orgName,
          ownerName: form.ownerName,
          billingCycle: form.billingCycle,
          plan: form.plan,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409 && data?.detail?.code === 'account_exists') {
        toast.message('Account already exists', { description: 'Sign in instead.' });
        navigate(`/login?email=${encodeURIComponent(data.detail.email || email)}`);
        return;
      }
      if (!res.ok) {
        const msg = apiErrorDetail(data, 'Could not send verification code');
        uiDebugLog('Signup.jsx:requestOtp', 'request otp failed', { code: res.status, msg }, 'G');
        throw new Error(msg);
      }
      setStep('verify');
      setOtp('');
      uiDebugLog(
        'Signup.jsx:requestOtp',
        'otp step shown',
        { emailSent: data.emailSent, hasDebugCode: Boolean(data.debugCode), plan: form.plan },
        'G',
      );
      toast.success(data.emailSent ? 'Verification code sent' : 'Code created', {
        description: data.emailSent
          ? `Check ${email} for your 6-digit code.`
          : 'Email delivery failed — contact support or try again.',
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    const email = String(form.email || '').trim();
    if (otp.length !== 6) {
      toast.error('Enter the 6-digit code from your email');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/signup/verify`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, code: otp }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = apiErrorDetail(data, 'Verification failed');
        uiDebugLog('Signup.jsx:verifyOtp', 'verify failed', { code: res.status, msg }, 'G');
        throw new Error(msg);
      }
      uiDebugLog('Signup.jsx:verifyOtp', 'verify ok', { tenant: data.tenantId, plan: data.plan }, 'G');
      finishSignup(data, 'signup');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const resendOtp = async () => {
    const email = String(form.email || '').trim();
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/signup/request-otp`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email,
          password: form.password,
          orgName: form.orgName,
          ownerName: form.ownerName,
          billingCycle: form.billingCycle,
          plan: form.plan,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = typeof data?.detail === 'string' ? data.detail : 'Could not resend code';
        throw new Error(msg);
      }
      toast.success(data.emailSent ? 'New code sent' : 'Code refreshed', {
        description: data.emailSent ? `Check ${email}` : 'Email delivery failed',
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthPageShell documentTitle={SIGNUP_DOCUMENT_TITLE}>
      {step === 'details' ? (
        <form onSubmit={requestOtp} data-testid="signup-form" className="auth-login-form">
          <h1 className="text-[2rem] sm:text-[2.25rem] font-semibold tracking-tight text-[#0a0a0a] leading-tight">
            Create Account
          </h1>
          <p className="mt-2 text-[15px] text-[#6b7280]">
            Choose a package and start your 14-day trial.
          </p>

          <div className="mt-8 space-y-5">
            <fieldset data-testid="signup-plan-picker">
              <legend className="block text-sm font-medium text-[#111827] mb-3">Package</legend>
              <div className="flex flex-col gap-2">
                {plans.map((plan) => {
                  const prices = planPrices(plan);
                  const selected = form.plan === plan.id;
                  const priceLabel =
                    form.billingCycle === 'annual'
                      ? formatMoneyWithPeriod(prices.annualDiscounted, 'yr')
                      : formatMoneyWithPeriod(prices.monthly, 'mo');
                  return (
                    <label
                      key={plan.id}
                      className={`auth-plan-option flex items-start gap-3 cursor-pointer rounded-2xl border px-3.5 py-3 transition-colors ${
                        selected ? 'border-[#111827] bg-[#f9fafb]' : 'border-[#e5e7eb] bg-white hover:border-[#d1d5db]'
                      }`}
                    >
                      <input
                        type="radio"
                        name="plan"
                        className="mt-1 auth-login-check"
                        checked={selected}
                        onChange={() => {
                          setForm((p) => ({ ...p, plan: plan.id }));
                          const url = new URL(window.location.href);
                          url.searchParams.set('plan', plan.id);
                          window.history.replaceState({}, '', url.pathname + url.search);
                        }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-[#111827]">{plan.name}</span>
                          {plan.tag || plan.featured ? (
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-[#111827] bg-[#eef2ff] px-1.5 py-0.5 rounded">
                              {plan.tag || 'Popular'}
                            </span>
                          ) : null}
                        </span>
                        <span className="block text-sm text-[#111827] mt-0.5">
                          <span className="font-medium">{priceLabel}</span>
                          {form.billingCycle === 'annual' ? (
                            <span className="text-[#6b7280]"> after 17% off</span>
                          ) : null}
                        </span>
                        <span className="block text-[12px] text-[#6b7280] mt-0.5">{planLimitLine(plan)}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              <p className="mt-2 text-[12px] text-[#6b7280]">
                Need Enterprise?{' '}
                <Link to="/contact?intent=sales" className="text-[#111827] font-medium hover:underline">
                  Contact sales
                </Link>
                {' · '}
                <Link to="/compare" className="text-[#111827] font-medium hover:underline">
                  Compare plans
                </Link>
              </p>
            </fieldset>

            <fieldset>
              <legend className="block text-sm font-medium text-[#111827] mb-3">Billing after trial</legend>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className={`auth-billing-toggle ${form.billingCycle === 'monthly' ? 'is-active' : ''}`}
                  onClick={() => setForm((p) => ({ ...p, billingCycle: 'monthly' }))}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={`auth-billing-toggle ${form.billingCycle === 'annual' ? 'is-active' : ''}`}
                  onClick={() => setForm((p) => ({ ...p, billingCycle: 'annual' }))}
                >
                  Annual · 17% off
                </button>
              </div>
              <p className="mt-2 text-[12px] text-[#6b7280]">
                After trial: {selectedPlan?.name} at{' '}
                {form.billingCycle === 'annual'
                  ? formatMoneyWithPeriod(selectedPrices.annualDiscounted, 'yr')
                  : formatMoneyWithPeriod(selectedPrices.monthly, 'mo')}
              </p>
            </fieldset>

            <div>
              <label className="block text-sm font-medium text-[#111827] mb-1">Organization</label>
              <input
                className="auth-login-input"
                placeholder="Acme Events"
                required
                value={form.orgName}
                onChange={(e) => setForm((p) => ({ ...p, orgName: e.target.value }))}
                autoComplete="organization"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#111827] mb-1">Your name</label>
              <input
                className="auth-login-input"
                placeholder="Jordan Lee"
                value={form.ownerName}
                onChange={(e) => setForm((p) => ({ ...p, ownerName: e.target.value }))}
                autoComplete="name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#111827] mb-1">Work email</label>
              <input
                className="auth-login-input"
                placeholder="name@example.com"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                autoComplete="email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#111827] mb-1">Password</label>
              <PasswordField
                value={form.password}
                onChange={(password) => setForm((p) => ({ ...p, password }))}
                confirmValue={form.confirmPassword}
                onConfirmChange={(confirmPassword) => setForm((p) => ({ ...p, confirmPassword }))}
                showConfirm
                inputClassName="auth-login-input"
                passwordPlaceholder="Create a password"
                confirmPlaceholder="Confirm password"
                confirmLabel="Confirm password"
              />
            </div>

            <p className="text-[13px] text-[#6b7280] leading-relaxed">
              14-day trial on {selectedPlan?.name || 'your plan'} · 2 events · 500 attendees/event during trial.
              Billing starts automatically after trial on the cycle you choose.
            </p>

            <button type="submit" className="auth-login-primary w-full mt-1" disabled={busy} data-testid="signup-submit">
              {busy ? 'Sending code…' : `Continue with ${selectedPlan?.name || 'plan'}`}
            </button>

            <SocialAuthButtons
              intent="signup"
              disabled={busy}
              onProviderToken={handleSocialToken}
              className="auth-login-social"
              showDivider={false}
            />
          </div>

          <p className="mt-8 text-center text-sm text-[#6b7280]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#111827] font-semibold hover:underline">
              Log in
            </Link>
          </p>
        </form>
      ) : (
        <form onSubmit={verifyOtp} data-testid="signup-verify-form" className="auth-login-form">
          <h1 className="text-[2rem] sm:text-[2.25rem] font-semibold tracking-tight text-[#0a0a0a] leading-tight">
            Check your email
          </h1>
          <p className="mt-2 text-[15px] text-[#6b7280]">
            We sent a 6-digit code to <span className="font-medium text-[#111827]">{form.email}</span>.
          </p>
          <p className="mt-1 text-[13px] text-[#6b7280]">
            Starting {selectedPlan?.name} · {form.billingCycle === 'annual' ? 'annual' : 'monthly'} billing after trial
          </p>

          <div className="mt-8 space-y-5">
            <OtpCodeInput value={otp} onChange={setOtp} disabled={busy} variant="light" />
            <button
              type="submit"
              className="auth-login-primary w-full"
              disabled={busy || otp.length !== 6}
              data-testid="signup-verify-submit"
            >
              {busy ? 'Verifying…' : `Verify & start ${selectedPlan?.name || 'trial'}`}
            </button>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                className="text-[#111827] font-medium hover:underline cursor-pointer"
                onClick={() => setStep('details')}
                disabled={busy}
              >
                Edit details
              </button>
              <button
                type="button"
                className="text-[#6b7280] hover:text-[#111827] cursor-pointer"
                onClick={resendOtp}
                disabled={busy}
              >
                Resend code
              </button>
            </div>
          </div>
        </form>
      )}
    </AuthPageShell>
  );
}
